import React, { useState, useEffect } from 'react';
import { Activity, User, Calendar, Filter, Search, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { API_URL } from '../../services/api';

function AuditTrail() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filtres
  const [filters, setFilters] = useState({
    user_id: '',
    module: '',
    action: '',
    date_debut: '',
    date_fin: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Listes pour les dropdowns
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions] = useState(['CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT', 'VIEW', 'EXPORT']);

  useEffect(() => {
    loadLogs();
    loadStats();
    loadFilterOptions();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [page, filters]);

  // Auto-refresh toutes les 30s
  useEffect(() => {
    const interval = setInterval(() => {
      loadLogs();
      loadStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [page, filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, per_page: 30 });
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.module) params.append('module', filters.module);
      if (filters.action) params.append('action', filters.action);
      if (filters.date_debut) params.append('date_debut', filters.date_debut);
      if (filters.date_fin) params.append('date_fin', filters.date_fin);

      const res = await fetch(`${API_URL}/audit/?${params}`);
      const data = await res.json();
      let filteredLogs = data.logs || [];

      // Filtre texte cote client
      if (filters.search) {
        const s = filters.search.toLowerCase();
        filteredLogs = filteredLogs.filter(l =>
          (l.username || '').toLowerCase().includes(s) ||
          (l.detail || '').toLowerCase().includes(s) ||
          (l.module || '').toLowerCase().includes(s)
        );
      }

      setLogs(filteredLogs);
      setTotal(data.total || 0);
      setTotalPages(data.pages || 1);
    } catch (err) {
      console.error('Erreur chargement audit:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_URL}/audit/stats`);
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Erreur stats audit:', err);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const res = await fetch(`${API_URL}/users/`);
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : data.users || []);
    } catch (err) {
      console.error('Erreur chargement users:', err);
    }
    try {
      const res = await fetch(`${API_URL}/audit/stats`);
      const data = await res.json();
      if (data.par_module) {
        setModules(data.par_module.map(m => m.module).filter(Boolean));
      }
    } catch (err) {
      console.error('Erreur chargement modules:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ user_id: '', module: '', action: '', date_debut: '', date_fin: '', search: '' });
    setPage(1);
  };

  const getActionColor = (action) => {
    const a = (action || '').toUpperCase();
    if (a.includes('CREATE') || a.includes('INSERT')) return 'bg-green-100 text-green-800';
    if (a.includes('UPDATE') || a.includes('EDIT') || a.includes('MODIFY')) return 'bg-blue-100 text-blue-800';
    if (a.includes('DELETE') || a.includes('REMOVE')) return 'bg-red-100 text-red-800';
    if (a.includes('LOGIN') || a.includes('AUTH')) return 'bg-purple-100 text-purple-800';
    if (a.includes('LOGOUT')) return 'bg-gray-100 text-gray-800';
    if (a.includes('EXPORT')) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-700';
  };

  const todayCount = stats?.par_jour?.length > 0
    ? (stats.par_jour.find(j => j.jour === new Date().toISOString().split('T')[0])?.total || 0)
    : 0;
  const topUser = stats?.utilisateurs_actifs?.[0]?.username || '-';
  const topModule = stats?.par_module?.[0]?.module || '-';

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Activity className="w-8 h-8 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Journal d'audit</h1>
            <p className="text-gray-500 text-sm">Historique de toutes les actions du systeme</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${showFilters ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'bg-white border-gray-300 text-gray-700'} hover:bg-indigo-50 transition`}
          >
            <Filter className="w-4 h-4" /> Filtres
          </button>
          <button
            onClick={() => { loadLogs(); loadStats(); }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            <RefreshCw className="w-4 h-4" /> Actualiser
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Total actions</p>
          <p className="text-2xl font-bold text-gray-800">{total.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Actions aujourd'hui</p>
          <p className="text-2xl font-bold text-indigo-600">{todayCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border border-gray-100">
          <div className="flex items-center gap-1 text-sm text-gray-500"><User className="w-3 h-3" /> Utilisateur le plus actif</div>
          <p className="text-lg font-semibold text-gray-800 mt-1">{topUser}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Module le plus actif</p>
          <p className="text-lg font-semibold text-gray-800 mt-1">{topModule}</p>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow p-4 mb-6 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Utilisateur</label>
              <select
                value={filters.user_id}
                onChange={e => handleFilterChange('user_id', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Tous</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.nom_complet || u.username}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Module</label>
              <select
                value={filters.module}
                onChange={e => handleFilterChange('module', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Tous</option>
                {modules.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Action</label>
              <select
                value={filters.action}
                onChange={e => handleFilterChange('action', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Toutes</option>
                {actions.map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1"><Calendar className="w-3 h-3 inline" /> Date debut</label>
              <input
                type="date"
                value={filters.date_debut}
                onChange={e => handleFilterChange('date_debut', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1"><Calendar className="w-3 h-3 inline" /> Date fin</label>
              <input
                type="date"
                value={filters.date_fin}
                onChange={e => handleFilterChange('date_fin', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1"><Search className="w-3 h-3 inline" /> Recherche</label>
              <input
                type="text"
                placeholder="Rechercher..."
                value={filters.search}
                onChange={e => handleFilterChange('search', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <button onClick={resetFilters} className="text-sm text-gray-500 hover:text-indigo-600">
              Reinitialiser les filtres
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
            <span className="ml-2 text-gray-500">Chargement...</span>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucune action trouvee</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Date / Heure</th>
                  <th className="text-left px-4 py-3 font-medium">Utilisateur</th>
                  <th className="text-left px-4 py-3 font-medium">Action</th>
                  <th className="text-left px-4 py-3 font-medium">Module</th>
                  <th className="text-left px-4 py-3 font-medium">Detail</th>
                  <th className="text-left px-4 py-3 font-medium">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log, idx) => (
                  <tr key={log.id || idx} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {log.created_at || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-700">{log.username || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                        {log.action || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{log.module || '-'}</td>
                    <td className="px-4 py-3 text-gray-500 max-w-xs truncate" title={log.detail}>
                      {log.detail || '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs font-mono">
                      {log.ip_address || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
            <p className="text-sm text-gray-500">
              Page {page} / {totalPages} ({total} resultats)
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded-lg text-sm disabled:opacity-40 hover:bg-white transition"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {/* Afficher quelques numeros de page */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const p = start + i;
                if (p > totalPages) return null;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-3 py-1 border rounded-lg text-sm transition ${p === page ? 'bg-indigo-600 text-white border-indigo-600' : 'hover:bg-white'}`}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 border rounded-lg text-sm disabled:opacity-40 hover:bg-white transition"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Info auto-refresh */}
      <p className="text-xs text-gray-400 mt-3 text-right">
        Actualisation automatique toutes les 30 secondes
      </p>
    </div>
  );
}

export default AuditTrail;
